import * as debug from 'debug';
import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { EventEmitter } from '../../includes/eventEmitter';
import { UserProvider } from '../user/user';
import {
  ActionSheetController,
  AlertController,
  ModalController,
  ToastController,
} from 'ionic-angular';
import { ToolsProvider } from '../tools/tools';
import { CrudApiProvider } from '../crud-api/crud-api';
import { UiToolsProvider } from '../ui-tools/ui-tools';
import { PushNotificationsProvider } from '../push-notifications/push-notifications';
import * as moment from 'moment';
import { UserVerificationProvider } from '../user-verification/user-verification';
import { MessagesProvider } from '../messages/messages';

declare let CmKit: any;

@Injectable()
export class FriendshipsProvider extends EventEmitter {
  log = debug('app:FriendshipsProvider');

  constructor(
    public user: UserProvider,
    public modalCtrl: ModalController,
    public tools: ToolsProvider,
    public crudApi: CrudApiProvider,
    public actionSheetCtrl: ActionSheetController,
    public alertCtrl: AlertController,
    public uiTools: UiToolsProvider,
    public toastCtrl: ToastController,
    public push: PushNotificationsProvider,
    public userVerification: UserVerificationProvider,
    public messaging: MessagesProvider
  ) {
    super();
  }

  async requestFriendship(friend): Promise<any> {
    if (!this.user.userPermission('friends')) {
      return this.tools.loginBlock(
        'You must be logged in to request a friend.'
      );
    }
    this.log('requesting friendship');

    try {
      let update = {
        userA: _.get(this.user, 'info._id'),
        userB: _.get(friend, '_id'),
      };

      this.tools.logAnalyticsEvent('trackCustom', 'request-friend');

      const { data, error } = await this.crudApi.post('/api/friends', update);

      let canSendPushNotification =
        await this.tools.checkUsersPushNotificationSettings(
          friend._id,
          'friendRequests'
        );
      if (canSendPushNotification) {
        this.push.init();
      } else {
        this.log(
          'no, user has push notifications turned off for friend requests'
        );
      }

      return Promise.resolve(data ? data : null);
    } catch (error) {
      console.warn('Error making friends: ', error);
    }
  }

  acceptFriendship(friendshipId): Promise<any> {
    this.tools.logAnalyticsEvent('trackCustom', 'accept-friend');
    return this.crudApi
      .put(`/api/friends/${friendshipId}`, {
        accepted: true,
      })
      .then(res => {
        this.log('res', res);
        this.push.init();
        this.user.reloadFriends();
        return res.data;
      })
      .catch(e => {
        this.log('error', e);
        return {};
      });
  }

  removeFriendship(userA, userB) {
    this.crudApi.delete(`/api/friends/${userA}/${userB}`).then(
      response => {
        this.log('Delete Response: ', response);
        this.user.reloadFriends();
        this.tools.logAnalyticsEvent('trackCustom', 'remove-friend', {
          user: _.get(this.user, 'info._id'),
        });
      },
      e => {
        this.log('An error occured. API could not delete friendship: ', e);
      }
    );
  }

  getFriendship(userA, userB): Promise<any> {
    return this.crudApi
      .get(`/api/friends/${userA}/${userB}`)
      .then(res => {
        this.log('friendship', res.data);
        return Promise.resolve(res.data);
      })
      .catch(e => {
        this.log('The user could not be found.');
        this.log('error', e);
        return Promise.resolve({});
      });
  }

  getConversation(userId): Promise<any> {
    return this.crudApi
      .get('/api/conversations', {
        $or: [{ userA: userId }, { userB: userId }],
      })
      .then(res => {
        return res.data;
      })
      .catch(e => {
        this.log('error getting conversation');
        const toast = this.toastCtrl.create({
          message:
            'An error occurred while retrieving this conversation. Please check your connection and try again. If the issue continues, please contact us for help.',
          position: 'top',
          duration: 5000,
        });
        toast.present();
      });
  }

  getFriendsForUser(userId): Promise<any> {
    return this.crudApi.get(`/api/friends/${userId}`).then(res => {
      return res.data;
    });
  }

  async userProfileOtherActionSheet(userProfile) {
    let nameOUser = userProfile.firstName;
    let buttons = [];
    let adminMode = false;
    let moderatorMode = false;
    let userMode = true;

    // set user mode
    if (this.user.info.role === 'admin') {
      adminMode = true;
      moderatorMode = true;
    } else if (
      this.user.info.role !== 'admin' &&
      this.user.info.subRole === 'contentModerator'
    ) {
      moderatorMode = true;
    }

    if (adminMode) {
      buttons.push({
        sectionTitle: ' ',
      });

      buttons.push({
        sectionTitle: `Admin Options`,
      });
      if (userProfile.states.verifiedStatus === 'verified') {
        buttons.push({
          text: 'Message Verified User',
          icon: `<i style="font-size:16px;" class="fal fa-comment-alt-plus"></i>`,

          handler: () => {
            this.tools.checkCanMessage().then(canMessage => {
              if (canMessage) {
                this.messaging
                  .createNewConversation([userProfile._id], '', 'profile')
                  .then(convo => {
                    // TODO navTo
                    // this.tools.openExternalUrl(
                    //   `/contacts/friends-message/${convo._id}`,
                    //   'deep-link',
                    //   {
                    //     conversation: convo,
                    //   }
                    // );
                    this.tools.navTo('FriendsMessagePage', 2, {
                      conversation_id: convo._id,
                      conversation: convo,
                    });
                  });
              }
            });
          },
        });
      } else {
        buttons.push({
          text: 'Messaging disabled: This user is not verified',
          icon: `<i  style="font-size:16px;"  class="fas fa-ban"></i>`,
        });
      }

      buttons.push(
        {
          text: 'Suspend User for 24 Hours',
          subText:
            _.get(userProfile, 'stats.temporaryBanCount', 0) < 3
              ? 'Temporary suspension'
              : 'The user has been suspended more than 3 times, ban user indefinitely?',
          icon: `<svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M1.5 15C1.21875 15 1 14.7812 1 14.5V13.2188C1 11.4375 2.40625 10.0312 4.1875 10C4.8125 10 5.40625 10.5 7 10.5C8.5625 10.5 9.1875 10 9.78125 10C9.875 10 9.9375 10.0312 10.0312 10.0312C10.0938 9.75 10.2812 9.3125 10.4375 9.09375C10.25 9.0625 9.96875 9.03125 9.78125 9C8.875 9 8.46875 9.5 6.96875 9.5C5.5 9.5 5.09375 9 4.1875 9C1.875 9 0 10.9062 0 13.2188V14.5C0 15.3438 0.65625 16 1.5 16H10.5C10.3125 15.7812 10.0938 15.3125 10.0312 15H1.5ZM7 8C9.1875 8 11 6.21875 11 4C11 1.8125 9.1875 0 7 0C4.78125 0 3 1.8125 3 4C3 6.21875 4.78125 8 7 8ZM7 1C8.625 1 10 2.375 10 4C10 5.65625 8.625 7 7 7C5.34375 7 4 5.65625 4 4C4 2.375 5.34375 1.03125 7 1ZM15.5 11.5C14.9375 11.5 14.5 11.9688 14.5 12.5C14.5 13.0625 14.9375 13.5 15.5 13.5C16.0312 13.5 16.5 13.0625 16.5 12.5C16.5 11.9688 16.0312 11.5 15.5 11.5ZM18.5 9H18V7.5C18 6.125 16.875 5 15.5 5C14.0938 5 13 6.125 13 7.5V9H12.5C11.6562 9 11 9.6875 11 10.5V14.5C11 15.3438 11.6562 16 12.5 16H18.5C19.3125 16 20 15.3438 20 14.5V10.5C20 9.6875 19.3125 9 18.5 9ZM14 7.5C14 6.6875 14.6562 6 15.5 6C16.3125 6 17 6.6875 17 7.5V9H14V7.5ZM19 14.5C19 14.7812 18.75 15 18.5 15H12.5C12.2188 15 12 14.7812 12 14.5V10.5C12 10.25 12.2188 10 12.5 10H18.5C18.75 10 19 10.25 19 10.5V14.5Z" fill="#262626"/>
</svg>`,
          handler: () => {
            this.banUserTimed(userProfile);
          },
        },

        {
          text:
            userProfile.states.isBanned === true
              ? 'Restore User Account'
              : 'Ban User Indefinitely',
          icon:
            userProfile.states.isBanned === true
              ? `<i class="fal fa-undo"></i>`
              : `<i class="fal fa-fw fa-user-slash"></i>`,
          handler: () => {
            this.banUser(userProfile);
          },
        }
      );
    }

    if (moderatorMode) {
      if (!adminMode) {
        buttons.push({
          sectionTitle: ' ',
        });
      }
      buttons.push({
        sectionTitle: 'Moderator Options',
      });
      buttons.push({
        text: 'Escalate to CDLLife',
        icon: `
          <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 10H15.9375L15.1875 4.21875C15.0312 3 13.9375 2.03125 12.7188 2H7.25C6.03125 2.03125 4.9375 3 4.78125 4.21875L4.03125 10H4C3.4375 10 3 10.4688 3 11V13C3 13.5625 3.4375 14 4 14H16C16.5312 14 17 13.5625 17 13V11C17 10.4688 16.5312 10 16 10ZM5.75 4.3125C5.84375 3.59375 6.53125 3.03125 7.25 3H12.7188C13.4375 3.03125 14.125 3.59375 14.2188 4.3125L14.9062 10H7.21875L7.96875 4.375C7.96875 4.34375 8 4.34375 8 4.34375C8 4.21875 7.875 4.09375 7.78125 4.09375L7.28125 4.03125C7.25 4 7.25 4 7.25 4C7.125 4 7 4.125 7 4.21875L6.21875 10H5.0625L5.75 4.3125ZM16 13H4V11H16V13ZM2.5 5H0.5C0.21875 5 0 5.25 0 5.5C0 5.78125 0.21875 6 0.5 6H2.5C2.75 6 3 5.78125 3 5.5C3 5.25 2.75 5 2.5 5ZM19.5 5H17.5C17.2188 5 17 5.25 17 5.5C17 5.78125 17.2188 6 17.5 6H19.5C19.75 6 20 5.78125 20 5.5C20 5.25 19.75 5 19.5 5ZM1.25 0.96875L3.25 1.96875C3.3125 2 3.40625 2 3.5 2C3.75 2 4 1.78125 4 1.5C4 1.34375 3.875 1.15625 3.71875 1.0625L1.71875 0.0625C1.65625 0.03125 1.5625 0 1.5 0C1.21875 0 0.96875 0.25 0.96875 0.5C0.96875 0.6875 1.09375 0.875 1.25 0.96875ZM16.5 2C16.5625 2 16.6562 2 16.7188 1.96875L18.7188 0.96875C18.875 0.875 19 0.6875 19 0.5C19 0.25 18.75 0 18.5 0C18.4062 0 18.3125 0.03125 18.25 0.0625L16.25 1.0625C16.0938 1.15625 15.9688 1.34375 15.9688 1.5C15.9688 1.78125 16.2188 2 16.4688 2H16.5Z" fill="#262626"/>
          </svg>
          `,
        handler: () => {
          this.reportUserAlert(userProfile);
        },
      });
      buttons.push({
        sectionTitle: ' ',
      });
    }

    if (userMode) {
      buttons.push(
        {
          icon: `<i class="far fa-fw fa-user-slash"></i>`,
          text: 'Block ' + nameOUser,
          subText: "The user won't be able to see you or contact you",
          handler: event => {
            this.blockUser(userProfile);
          },
        },
        {
          icon: `<i class="fal fa-fw fa-pennant"></i>`,
          text: 'Flag User',
          handler: event => {
            this.reportUserAlert(userProfile);
          },
        }
      );
    }

    await CmKit.ActionSheet({
      props: {
        templates: {
          title: this.uiTools.actionSheetUserTitle(userProfile),
        },
        buttons,
      },
    }).promise;
  }

  private reportUserAlert(userProfile) {
    let alert = this.alertCtrl.create({
      title: 'Report User',
      message: "Please explain why you'd like to report this user.",
      inputs: [
        {
          name: 'reason',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          cssClass: 'alert-button-cancel',
          role: 'cancel',
          handler: data => {
            this.log('Cancel clicked');
          },
        },
        {
          text: 'Report',
          cssClass: 'alert-button-confirmatory',
          handler: data => {
            this.reportUser(data.reason, userProfile)
              .then(res => {
                this.toastConfirmReportedUser(true);
              })
              .catch(e => {
                this.toastConfirmReportedUser(false);
                this.log('could not report user');
              });
          },
        },
      ],
      cssClass: 'alert-title',
    });
    alert.present();
  }

  private reportUser(reason, userProfile) {
    let reportedUser = _.get(userProfile, '_id');
    let report = {
      reportedUser: reportedUser,
      message: reason,
      createdAt: new Date(),
    };

    this.tools.logAnalyticsEvent('trackCustom', 'user-report', {
      user: _.get(this.user, 'info._id'),
      message: reason,
    });

    return this.crudApi
      .post(`/api/users/${reportedUser}/reports`, report)
      .then(res => {
        this.log('report response:', res);
      })
      .catch(e => {
        this.log('report response error ', e);
      });
  }

  private blockUser(userProfile) {
    const self = this;
    let alert = this.alertCtrl.create({
      title: 'Are you sure?',
      subTitle: this.tools.getBlockUserWarning(
        _.get(this, 'post.user.firstName')
      ),
      buttons: [
        {
          text: 'Cancel',
          cssClass: 'alert-button-cancel',
          role: 'cancel',
          handler: data => {
            this.log('Cancel clicked');
          },
        },
        {
          text: 'Block User',
          cssClass: 'alert-button-confirmatory',
          handler: data => {
            const profileUserId = _.get(userProfile, '_id') || null;

            if (!profileUserId) {
              const toast = this.toastCtrl.create({
                message: 'Could not block the user.',
                duration: 3000,
              });
              toast.present();
              return;
            }
            this.crudApi
              .put(`/api/users/${this.user.get('_id')}`, {
                blockUser: profileUserId,
              })
              .then(res => {
                this.log('blocked User response: ', res);
                const toast = this.toastCtrl.create({
                  message: 'The user has been blocked.',
                  duration: 3000,
                });
                this.user.info.blockedUsers =
                  _.get(res, 'data.blockedUsers') || [];
                this.user.emit('hideShowContent');
                toast.present();
                self.emit('popAll');
              })
              .catch(e => {
                const toast = this.toastCtrl.create({
                  message: 'Could not block the user.',
                  duration: 3000,
                });
                toast.present();
                this.log('blocked user failed: ', e);
              });
          },
        },
      ],
      cssClass: 'alert-title',
    });

    alert.present();
  }

  private banUserTimed(userProfile) {
    const userId = _.get(userProfile, '_id');
    let timesBanned = _.get(userProfile, 'stats.temporaryBanCount', 0);
    let status = _.get(userProfile, 'states.isBanned');
    let expirationTime = moment().add(24, 'h').toString();
    let date = new Date(expirationTime);

    if (!status && timesBanned < 3) {
      this.crudApi
        .post(`/api/users/${userId}/ban`, {
          expiration: date,
        })
        .then(response => {
          if (response) {
            _.set(userProfile, 'states.isBanned', true);
          }
        })
        .catch(e => {
          this.log('error');
        });
    } else if (!status && timesBanned >= 3) {
      this.crudApi
        .post(`/api/users/${userId}/ban`)
        .then(response => {
          if (response) {
            _.set(userProfile, 'states.isBanned', true);
          }
        })
        .catch(e => {
          this.log('error');
        });
    } else {
      console.warn('user is already banned');
      return;
    }
  }

  private banUser(userProfile) {
    let status = _.get(userProfile, 'states.isBanned');
    const userId = _.get(userProfile, '_id');

    if (!status) {
      this.crudApi
        .post(`/api/users/${userId}/ban`)
        .then(response => {
          if (response) {
            _.set(userProfile, 'states.isBanned', true);
          }
        })
        .catch(e => {
          this.log('error');
        });
    } else {
      this.crudApi
        .delete(`/api/users/${userId}/ban`)
        .then(response => {
          if (response) {
            _.set(userProfile, 'states.isBanned', false);
          }
        })
        .catch(e => {
          this.log('error');
        });
    }
  }

  private toastConfirmReportedUser(isSuccess) {
    let toast;

    if (isSuccess) {
      toast = this.toastCtrl.create({
        message:
          'This user has been reported to CDLLife. Thank you for helping out our community!',
        duration: 3000,
        position: 'top',
      });
    } else {
      toast = this.toastCtrl.create({
        message:
          'The report could not be delivered to CDLLife. Please try again later ',
        duration: 3000,
        position: 'top',
      });
    }

    toast.onDidDismiss(() => {
      this.log('Dismissed toast');
    });

    toast.present();
  }

  private openBadges(userProfile) {
    this.emit('UserProfileViewBadgesPage');
  }
}



// WEBPACK FOOTER //
// ./src/providers/friendships/friendships.ts